import { useMemo, useState } from "react";
import { Form, Input, InputNumber, Select, Row, Col, Spin } from "antd";
import styles from "./LaboratoryForm.module.scss";
import Layout from "../../../Containers/Layout";
import { Redirect, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getLaboratory, updateLaboratory, addLaboratory } from "../../../Requests/laboratory-requests";
import { NOTIFICATION_TYPES, openNotification } from "../../../Components/Notifications/NotificationsUtils";
import { getUserId, useIsAdmin } from "../../../utils/utilFunctions";
import { theme } from "../../../theme";
import ConfirmationModal from "../../../Containers/ConfirmationModal";
import { getFaculties } from "../../../Requests/faculty-requests";
import { getAllAcademicUsers, getUserById } from "../../../Requests/academic-user-requests";
import { AcademicUserNameDTO, LaboratoryAddDTO, LaboratoryCategoryEnum, LaboratoryTypeEnum } from "../../../Api";
import useDebounce from "../../../Hooks/debounce";

const { Option } = Select;

const LaboratoryForm = (props: any) => {
  const { t } = useTranslation();
  
  const currentUserId = getUserId();
  const isAdmin = useIsAdmin();
  const { laboratoryId } = useParams<{ laboratoryId: string }>();
  const [redirect, setRedirect] = useState(false);
  const [responsibleSearch, setResponsibleSearch] = useState("");
  const debouncedResponsibleSearch = useDebounce(responsibleSearch, 600);


  const [form] = Form.useForm<{
          number: string | null,
          area: number | null,
          volume?: number | null,
          capacity: number | null,
          name: string | null,
          type: LaboratoryTypeEnum,
          category: LaboratoryCategoryEnum,
          responsibleUserId?: string | null,
          description: string | null,
          resources: string | null,
          facultyId: string
  }>();
  const watchedType = Form.useWatch("type", form);

  const openGetErrorNotification = (_error: any) => {
    openNotification(
      'Eroare',
      'Nu s-au putut prelua datele de pe server!',
      NOTIFICATION_TYPES.ERROR
    );
  };

  const { data: user } = useQuery(['getAcademicUser'],
    () => {
        return getUserById(currentUserId);
    },
    {
      onSuccess: (data) => {
        // default faculty from current user, but allow editing
        if (!laboratoryId) {
          form.setFieldValue("facultyId", data?.department?.facultyId);
        }
      },
        onError: openGetErrorNotification,
    }
  );

  const { data: laboratory } = useQuery(["getLaboratory", laboratoryId], () => getLaboratory(laboratoryId), {
    onError: (err) => {
      openGetErrorNotification(err);
    },
    onSuccess: (data) => {          
      if (data) {
        form.setFieldsValue({
          number: data.number,
          area: data.area,
          volume: data.volume,
          capacity: data.capacity,
          name: data.name,
          type: data.type,
          category: data.category,
          responsibleUserId: data.responsibleUserId ?? null,
          description: data.description,
          resources: data.resources,
          facultyId: data.facultyId
        });
      }
    },
    enabled: !!laboratoryId,
    refetchOnWindowFocus: false
  });

  const { data: faculties } = useQuery(['getFaculties'],
    () => {
        return getFaculties(undefined, undefined, 1, 50);
    },
    {
        onError: openGetErrorNotification,
    }
);

  const { data: responsibleUsersRes, isLoading: isLoadingResponsibleSearch } = useQuery(
    ["getResponsibleUsers", debouncedResponsibleSearch],
    () => getAllAcademicUsers(debouncedResponsibleSearch, 1, 20, undefined),
    { onError: openGetErrorNotification, refetchOnWindowFocus: false }
  );

  const responsibleUsers = useMemo(() => {
    const current: AcademicUserNameDTO[] = laboratory?.responsibleUser
      ? [{ id: laboratory.responsibleUser.id, name: laboratory.responsibleUser.name, email: laboratory.responsibleUser.email } as any]
      : [];
    const fromSearch = (responsibleUsersRes as any)?.data || [];
    const seen = new Set<string>();
    const merged = [...current, ...fromSearch].filter((u: any) => {
      if (!u?.id) return false;
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    });
    return merged;
  }, [laboratory?.responsibleUser, responsibleUsersRes]);



  const openSaveErrorNotification = (error: any) => {
    openNotification(
      'Eroare!',
      'Nu s-a putut salva sala!',
      NOTIFICATION_TYPES.ERROR
    );
  };

  const handleCancel = () => {
    setRedirect(true);
  };

  const openSuccessNotificationAndRedirect = () => {
    setRedirect(true);
    openNotification(
      'Succes!',
      'Sala a fost salvata cu succes!',
      NOTIFICATION_TYPES.SUCCESS,
      6
    );
  };

  const handleSave = () => {
    form.validateFields()
      .then((values) => {
        const resolvedFacultyId = isAdmin ? values.facultyId : user?.department?.facultyId;
        if (!resolvedFacultyId) {
          openNotification(
            'Eroare!',
            'Nu s-a putut determina facultatea utilizatorului.',
            NOTIFICATION_TYPES.ERROR
          );
          return;
        }

        const laboratoryData: LaboratoryAddDTO = {
          number: values.number,
          area: values.area ?? undefined,
          volume: values.volume ?? undefined,
          capacity: values.capacity ?? undefined,
          name: values.name,
          type: values.type,
          // category + responsible only relevant for laboratories
          category: values.type === LaboratoryTypeEnum.Laboratory ? values.category : undefined,
          responsibleUserId: values.type === LaboratoryTypeEnum.Laboratory ? values.responsibleUserId ?? null : null,
          description: values.description,
          resources: values.resources,
          facultyId: resolvedFacultyId,
        };

        const saveAction = laboratoryId
          ? updateLaboratory(laboratoryId, laboratoryData)
          : addLaboratory(laboratoryData);

        saveAction.then(openSuccessNotificationAndRedirect).catch(openSaveErrorNotification);
      })
      .catch(() => {
        openNotification(
          'Eroare!',
          'Vă rugăm să completați toate câmpurile obligatorii!',
          NOTIFICATION_TYPES.ERROR
        );
      });
  };

  if (redirect) {
    return <Redirect to="/laboratoare" />;
  }

  return (
    <Layout>
      <div className="container">
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="number"
                label="Număr curent"
                rules={[{ required: true, message: 'Vă rugăm să introduceți numărul sălii!' }]}
              >
                <Input placeholder="Numărul sălii" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="area"
                label="Suprafață (m²)"
                rules={[{ required: true, message: 'Vă rugăm să introduceți suprafața!' }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Suprafața sălii"
                  step={0.01}
                  min={0}
                  decimalSeparator=","
                  parser={(value) => value?.replace(',', '.') as any}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="volume"
                label="Volum"
              >
                <InputNumber style={{ width: "100%" }} placeholder="Volumul sălii" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Tip"
                rules={[{ required: true, message: 'Vă rugăm să selectați un tip!' }]}
              >
                <Select
                  placeholder="Selectați tipul"
                  onChange={(v) => {
                    if (v !== LaboratoryTypeEnum.Laboratory) {
                      form.setFieldsValue({ category: undefined as any, responsibleUserId: null });
                    }
                  }}
                >
                  <Option value={LaboratoryTypeEnum.Laboratory}>Laborator</Option>
                  <Option value={LaboratoryTypeEnum.Seminar}>Seminar</Option>
                  <Option value={LaboratoryTypeEnum.Curs}>Curs</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Nume"
                rules={[{ required: true, message: 'Vă rugăm să introduceți numele sălii!' }]}
              >
                <Input placeholder="Numele sălii" />
              </Form.Item>
            </Col>
            {watchedType === LaboratoryTypeEnum.Laboratory ? (
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="Categorie"
                  rules={[{ required: true, message: 'Vă rugăm să selectați o categorie!' }]}
                >
                  <Select placeholder="Selectați o categorie">
                    <Option value={LaboratoryCategoryEnum.Technological}>Tehnologic</Option>
                    <Option value={LaboratoryCategoryEnum.Informatic}>Informatic</Option>
                  </Select>
                </Form.Item>
              </Col>
            ) : (
              <Col span={12} />
            )}
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="capacity"
                label="Capacitate (nr. de locuri)"
                rules={[{ required: true, message: 'Vă rugăm să introduceți numărul de locuri!' }]}
              >
                <InputNumber style={{ width: "100%" }} placeholder="Capacitatea sălii" min={0} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="facultyId"
                label="Facultate"
                rules={[{ required: true, message: 'Vă rugăm să selectați facultatea!' }]}
              >
                <Select
                  placeholder="Selectați facultatea"
                  allowClear={isAdmin}
                  disabled={!isAdmin}
                >
                          {
                          faculties?.map((option: any, index) => (
                              <Option key={option.id} value={option.id}>{option.name}</Option>
                          )) ?? [] }
                </Select>
              </Form.Item>
            </Col>

          </Row>

          {watchedType === LaboratoryTypeEnum.Laboratory ? (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="responsibleUserId"
                  label="Responsabil sală"
                  rules={[{ required: true, message: 'Vă rugăm să selectați responsabilul sălii!' }]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Căutare responsabil"
                    filterOption={false}
                    onSearch={setResponsibleSearch}
                    notFoundContent={isLoadingResponsibleSearch ? <Spin size="small" /> : null}
                  >
                    {(responsibleUsers || []).map((u: any) => (
                      <Option key={u.id} value={u.id}>
                        {u.name} - {u.email}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          ) : null}
          <Row gutter={16}>
            
            <Col span={24}>
              <Form.Item
                name="resources"
                label="Resurse"
                rules={[{ required: true, message: 'Vă rugăm să introduceți resursele!' }]}
              >
                <Input.TextArea placeholder="Resursele sălii" rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Descriere"
                rules={[{ required: true, message: 'Vă rugăm să introduceți descrierea!' }]}
              >
                <Input.TextArea placeholder="Descrierea sălii" rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
          <div className={styles.secondLine}>
              <div className={styles.firstColumn} style={{ display: "flex", width: "250px", justifyContent: "space-between " }}>
                <ConfirmationModal
                  nameButton={t('internships.addInternshipForm.cancel')}
                  modalText={t('internships.cancelMessage')}
                  titleButton={''}
                  handleFunction={handleCancel}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.green, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
                <ConfirmationModal
                  nameButton={laboratoryId ? t('internships.addInternshipForm.updateButton') : t('internships.addInternshipForm.saveButton')}
                  modalText={t('internships.saveMessage')}
                  titleButton={''}
                  handleFunction={handleSave}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.secondColor, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
              </div>
            </div>
        </Row>
        </Form>
      </div>
    </Layout>
  );
};

export default LaboratoryForm;